import React, { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Minus, UserPlus, Search, LoaderCircle, CheckCircle2, X, Users } from "lucide-react";
import { cn, formatCurrency } from "@/lib/utils";
import { api } from "@/lib/api";

interface Guarantor {
  id: number;
  memberId?: number;
  name: string;
  memberCode: string;
  phone: string;
  email: string;
  savings: number;
  verified?: boolean;
}

interface GuarantorFieldsProps {
  guarantors: Guarantor[];
  setGuarantors: (guarantors: Guarantor[]) => void;
  requiredCount: number;
  maxCount?: number;
  className?: string;
  disabled?: boolean;
  isAdmin?: boolean;
  fetchMemberSavings?: (memberCode: string) => Promise<number>;
}

interface SearchResult {
  id: number;
  fullNames: string;
  memberCode: string;
  phone: string;
  email: string;
  savings: number;
}

function MemberSelector({
  onSelect,
  excludeIds,
  disabled,
}: {
  onSelect: (m: SearchResult) => void;
  excludeIds: number[];
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [allMembers, setAllMembers] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    inputRef.current?.focus();
    if (!loaded) {
      setLoading(true);
      api.get("/members/search-guarantors", { params: { limit: 200 } })
        .then(({ data }) => {
          setAllMembers(data.data || []);
          setLoaded(true);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [open]);

  const filtered = React.useMemo(() => {
    let list = allMembers.filter((m) => !excludeIds.includes(m.id));
    if (query.length >= 2) {
      const q = query.toLowerCase();
      list = list.filter(
        (m) =>
          m.fullNames?.toLowerCase().includes(q) ||
          m.memberCode?.toLowerCase().includes(q) ||
          m.phone?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [allMembers, excludeIds, query]);

  const noEligible = loaded && allMembers.filter((m) => !excludeIds.includes(m.id)).length === 0;
  const noSearchResults = loaded && query.length >= 2 && filtered.length === 0 && !noEligible;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setSelectedIndex((p) => Math.min(p + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSelectedIndex((p) => Math.max(p - 1, -1)); }
    else if (e.key === "Enter" && selectedIndex >= 0 && filtered[selectedIndex]) {
      e.preventDefault();
      onSelect(filtered[selectedIndex]);
      setOpen(false);
      setQuery("");
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  if (open) {
    return (
      <div ref={containerRef} className="w-full">
        <div className="fixed inset-0 bg-black/20 z-40" onClick={() => { setOpen(false); setQuery(""); }} />
        <div className="relative z-50 w-[min(90vw,900px)] mx-auto">
          <div className="bg-white border border-slate-200 rounded-2xl shadow-2xl">
            <div className="p-5 border-b border-slate-100">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm font-semibold text-slate-700">Select a Guarantor</p>
                <button
                  type="button"
                  className="text-slate-400 hover:text-slate-600 p-1"
                  onClick={() => { setOpen(false); setQuery(""); }}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  ref={inputRef}
                  placeholder="Search by name, code, or phone..."
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setSelectedIndex(-1); }}
                  onKeyDown={handleKeyDown}
                  className="h-11 text-base pl-11"
                />
              </div>
            </div>
            <div className="max-h-[65vh] min-h-[300px] overflow-y-auto">
              {loading && (
                <div className="flex items-center justify-center py-12">
                  <LoaderCircle className="h-8 w-8 animate-spin text-slate-300" />
                </div>
              )}
              {!loading && noEligible && (
                <div className="flex flex-col items-center justify-center py-12">
                  <Users className="h-12 w-12 text-slate-200 mb-3" />
                  <p className="text-base text-slate-400 font-medium">No eligible members available.</p>
                </div>
              )}
              {!loading && noSearchResults && (
                <div className="flex flex-col items-center justify-center py-12">
                  <Users className="h-12 w-12 text-slate-200 mb-3" />
                  <p className="text-base text-slate-400 font-medium">No members found.</p>
                </div>
              )}
              {!loading && !noEligible && filtered.map((m, i) => (
                <button
                  key={m.id}
                  type="button"
                  className={cn(
                    "w-full text-left px-5 py-4 text-sm hover:bg-green-50 flex items-center justify-between border-b border-slate-50 last:border-b-0 transition-all",
                    selectedIndex === i && "bg-green-50 ring-1 ring-green-200"
                  )}
                  onClick={() => { onSelect(m); setOpen(false); setQuery(""); }}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="h-11 w-11 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                      <Users className="h-5 w-5 text-green-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-800 truncate">{m.fullNames}</p>
                      <p className="text-sm text-slate-400 mt-0.5">
                        {m.memberCode && <span className="font-mono">{m.memberCode}</span>}
                        {m.memberCode && m.phone && <span className="mx-1.5">·</span>}
                        {m.phone && <span>{m.phone}</span>}
                      </p>
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-green-700 bg-green-50 px-3 py-1.5 rounded-lg shrink-0 ml-3 border border-green-100">
                    {formatCurrency(m.savings)}
                  </div>
                </button>
              ))}
              {!loading && !noEligible && query.length < 2 && filtered.length === 0 && (
                <div className="flex flex-col items-center justify-center py-8">
                  <p className="text-sm text-slate-400">Type at least 2 characters to search</p>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-center mt-3">
          <button
            type="button"
            className="text-sm text-slate-400 hover:text-slate-600 underline"
            onClick={() => { setOpen(false); setQuery(""); }}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef}>
      <Button
        type="button"
        variant="outline"
        size="default"
        className="gap-2 border-green-300 text-green-700 hover:bg-green-50 hover:text-green-800 hover:border-green-400 font-medium"
        onClick={() => setOpen(true)}
        disabled={disabled}
      >
        <Plus className="h-4 w-4" />
        Add Guarantor
      </Button>
    </div>
  );
}

export function GuarantorFields({
  guarantors,
  setGuarantors,
  requiredCount,
  maxCount = 3,
  className,
  disabled = false,
  isAdmin = false,
  fetchMemberSavings,
}: GuarantorFieldsProps) {
  const excludeIds = guarantors.map(g => g.memberId).filter((id): id is number => id != null);

  const removeGuarantor = (id: number) => {
    setGuarantors(guarantors.filter(g => g.id !== id));
  };

  const handleMemberSelect = (member: SearchResult) => {
    if (guarantors.length >= maxCount) return;
    setGuarantors([...guarantors, {
      id: Date.now(),
      memberId: member.id,
      name: member.fullNames,
      memberCode: member.memberCode || "",
      phone: member.phone || "",
      email: member.email || "",
      savings: member.savings,
      verified: true,
    }]);
  };

  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex items-center gap-2">
        <Users className="h-5 w-5 text-green-600" />
        <h3 className="text-sm font-semibold text-gray-800">
          Guarantors{requiredCount > 0 ? ` (${requiredCount} required)` : ""}
        </h3>
        {guarantors.length > 0 && (
          <span className="text-xs text-slate-400">({guarantors.length}/{maxCount})</span>
        )}
      </div>

      {guarantors.length > 0 && (
        <div className="space-y-2">
          {guarantors.map((g) => (
            <div key={g.id} className="flex items-center justify-between bg-white border border-slate-200 rounded-lg p-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                  <Users className="h-4 w-4 text-green-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{g.name}</p>
                  <p className="text-xs text-slate-400">
                    {g.memberCode && `${g.memberCode}`}
                    {g.savings > 0 && ` · ${formatCurrency(g.savings)} savings`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {g.verified && <CheckCircle2 className="h-4 w-4 text-green-500" />}
                {!disabled && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                    onClick={() => removeGuarantor(g.id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-center">
        {guarantors.length === 0 && (
          <div className="text-center space-y-3 py-2">
            <p className="text-sm text-slate-500">
              {requiredCount > 0
                ? `This loan type requires ${requiredCount} guarantor(s).`
                : "Add guarantors to strengthen your loan application."}
            </p>
            {!disabled && (
              <MemberSelector onSelect={handleMemberSelect} excludeIds={excludeIds} disabled={disabled} />
            )}
          </div>
        )}
        {guarantors.length > 0 && guarantors.length < maxCount && !disabled && (
          <MemberSelector onSelect={handleMemberSelect} excludeIds={excludeIds} disabled={disabled} />
        )}
      </div>
    </div>
  );
}

interface DocumentUploadProps {
  documents: File[];
  setDocuments: React.Dispatch<React.SetStateAction<File[]>>;
  acceptedTypes?: string[];
  maxSizeMB?: number;
  maxFiles?: number;
  className?: string;
  disabled?: boolean;
}

export function DocumentUpload({
  documents,
  setDocuments,
  acceptedTypes = ["application/pdf", "image/jpeg", "image/png", "image/jpg"],
  maxSizeMB = 10,
  maxFiles = 5,
  className,
  disabled = false,
}: DocumentUploadProps) {
  const [dragActive, setDragActive] = useState(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    if (disabled) return;
    const files = Array.from(e.dataTransfer.files);
    addFiles(files);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const files = Array.from(e.target.files || []);
    addFiles(files);
    e.target.value = "";
  };

  const addFiles = (files: File[]) => {
    const validFiles: File[] = [];
    for (const file of files) {
      if (!acceptedTypes.includes(file.type)) {
        alert(`File "${file.name}" is not a supported type. Allowed: PDF, JPG, PNG`);
        continue;
      }
      if (file.size > maxSizeMB * 1024 * 1024) {
        alert(`File "${file.name}" exceeds ${maxSizeMB}MB limit`);
        continue;
      }
      validFiles.push(file);
    }
    setDocuments((prev: File[]) => [...prev, ...validFiles].slice(0, maxFiles));
  };

  const removeDocument = (index: number) => {
    setDocuments((prev: File[]) => prev.filter((_: File, i: number) => i !== index));
  };

  return (
    <div className={cn("space-y-3", className)}>
      <label className="text-sm font-medium text-gray-700 block">Supporting Documents</label>
      <div
        className={cn(
          "border-2 border-dashed rounded-lg p-6 text-center transition-colors",
          dragActive ? "border-green-500 bg-green-50" : "border-gray-300 hover:border-gray-400",
          disabled && "opacity-50 cursor-not-allowed"
        )}
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragActive(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
        onDrop={handleDrop}
      >
        <input
          type="file"
          multiple
          accept={acceptedTypes.join(",")}
          onChange={handleFileSelect}
          className="hidden"
          id="document-upload"
          disabled={disabled || documents.length >= maxFiles}
        />
        <label htmlFor="document-upload" className="cursor-pointer">
          <div className="mx-auto w-12 h-12 text-gray-400">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
          </div>
          <p className="mt-2 text-sm text-gray-600">
            {dragActive ? "Drop files here..." : "Drag & drop files here, or click to browse"}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            PDF, JPG, PNG up to {maxSizeMB}MB each. Max {maxFiles} files.
          </p>
        </label>
      </div>
      {documents.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500">Attached Documents ({documents.length}/{maxFiles})</p>
          <div className="space-y-1">
            {documents.map((file, index) => (
              <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                <div className="flex items-center gap-2 text-sm">
                  {file.type.startsWith("image/") ? (
                    <svg className="h-4 w-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  ) : (
                    <svg className="h-4 w-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  )}
                  <div>
                    <p className="font-medium truncate max-w-[200px]">{file.name}</p>
                    <p className="text-xs text-gray-400">{(file.size / 1024).toFixed(1)} KB</p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-red-500 hover:bg-red-50"
                  onClick={() => removeDocument(index)}
                  disabled={disabled}
                >
                  <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
      {documents.length === 0 && !dragActive && (
        <p className="text-xs text-gray-400 text-center py-2">No documents attached</p>
      )}
    </div>
  );
}
